import { invitationPathFor } from "@hakili/contracts";
import { t } from "@hakili/i18n";
import { Button, Card } from "@hakili/ui";
import { useState } from "react";

/**
 * `P0-IDN-046`'s console window, beside `Choreographie`'s OTP card and built in
 * its shape on purpose: the two demonstration reads answer the same way, refuse
 * the same way, and should not look like different products.
 *
 * **Why it exists.** `identity.invitations` keeps only a hash, so the token
 * exists once in a return value, and no adapter sends the email that would carry
 * it. Step 3b of the banker's walk had no door until the card, and the runbook
 * carried an operator's database read in its place.
 *
 * **The refusal is one sentence for every cause**, because the ROUTE makes it
 * one: gate closed, gate unreadable, anonymous, wrong right, unknown address and
 * an absent path are a single `404`. Distinguishing them here would invent a
 * difference the API deliberately does not make , which is what the OTP card's
 * own header says about its four.
 *
 * **The address is not normalised here.** The keeper lowercases and trims what
 * it stores; this sends what was typed, `trim()`ed for what a paste leaves at
 * the ends. `P1-UIK-024` is the lesson behind that sentence: the phone card once
 * asked for a key the client never wrote, and the cure was to spell the value
 * with the SAME function rather than a second one that can drift.
 */
export function InvitationCard({
  lireLeLien,
}: {
  /** Reads the last token minted for an address. `null` when the API answers 404. */
  readonly lireLeLien: (email: string) => Promise<string | null>;
}): React.JSX.Element {
  const [adresse, setAdresse] = useState("");
  const [jeton, setJeton] = useState<string | null>(null);
  const [absent, setAbsent] = useState(false);
  const [enCours, setEnCours] = useState(false);

  return (
    <Card shape="console" className="flex flex-col gap-3">
      <h2 className="text-base font-semibold text-gray-900">{t("console.invitation.titre")}</h2>
      <p className="text-sm text-gray-500">{t("console.invitation.explication")}</p>
      <label className="flex flex-col gap-1 text-sm text-gray-500" htmlFor="invitation-adresse">
        {t("console.invitation.adresse")}
        <input
          id="invitation-adresse"
          className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
          inputMode="email"
          value={adresse}
          onChange={(event) => {
            setAdresse(event.target.value);
            // A new address clears the old answer rather than leaving a token
            // under a field it no longer belongs to , which with a credential
            // matters more than it does with a code.
            setJeton(null);
            setAbsent(false);
          }}
          data-testid="invitation-adresse"
        />
      </label>
      <Button
        variant="secondary"
        onClick={() => {
          setEnCours(true);
          void lireLeLien(adresse.trim())
            .then((lu) => {
              setJeton(lu);
              setAbsent(lu === null);
            })
            .finally(() => {
              setEnCours(false);
            });
        }}
      >
        {enCours ? t("console.invitation.lecture") : t("console.invitation.lire")}
      </Button>
      {jeton === null ? null : (
        <p
          role="status"
          className="break-all text-sm font-semibold text-gray-900"
          data-testid="invitation-jeton"
        >
          {t("console.invitation.copie")} : {invitationPathFor(jeton)}
        </p>
      )}
      {absent ? (
        <p role="alert" className="text-sm text-gray-500" data-testid="invitation-aucun">
          {t("console.invitation.aucun")}
        </p>
      ) : null}
    </Card>
  );
}
